import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FlowArrow, CheckCircle, Info, ArrowRight, Power } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { formatApiErrorDetail } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_DESC = {
  sign: '申請者本人が署名します',
  check: '管理者等が内容を確認します',
  approve: '管理者・承認者が最終承認します',
};

function sameStages(a, b) {
  if (a.length !== b.length) return false;
  return a.every((s, i) => s === b[i]);
}

export default function WorkflowSettings() {
  const [meta, setMeta] = useState({ stage_order: ['sign', 'check', 'approve'], stage_labels: {}, forms: [] });
  const [draft, setDraft] = useState({});       // form_type -> stages[]
  const [original, setOriginal] = useState({}); // form_type -> stages[]
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/workflow-config`);
      setMeta(r.data);
      setEnabled(r.data.enabled !== false);
      const map = {};
      (r.data.forms || []).forEach((f) => { map[f.form_type] = [...f.stages]; });
      setDraft(map);
      setOriginal(JSON.parse(JSON.stringify(map)));
    } catch (e) {
      toast.error('読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const order = meta.stage_order || ['sign', 'check', 'approve'];

  const toggle = (formType, stage) => {
    setDraft((prev) => {
      const cur = new Set(prev[formType] || []);
      if (cur.has(stage)) cur.delete(stage); else cur.add(stage);
      const next = order.filter((s) => cur.has(s)); // keep canonical order
      return { ...prev, [formType]: next };
    });
  };

  const save = async (formType) => {
    const stages = draft[formType] || [];
    if (stages.length === 0) {
      toast.error('少なくとも1つの段階を選択してください');
      return;
    }
    try {
      setSavingType(formType);
      await axios.put(`${API}/workflow-config/${formType}`, { stages });
      toast.success('ワークフローを保存しました');
      setOriginal((prev) => ({ ...prev, [formType]: [...stages] }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '保存に失敗しました');
    } finally {
      setSavingType(null);
    }
  };

  const reset = (formType) => {
    setDraft((prev) => ({ ...prev, [formType]: [...(original[formType] || [])] }));
  };

  const toggleEnabled = async (next) => {
    try {
      setToggling(true);
      await axios.put(`${API}/workflow-config-enabled`, { enabled: next });
      setEnabled(next);
      toast.success(next ? '承認ワークフローを有効にしました' : '承認ワークフローを無効にしました');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '設定の変更に失敗しました');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[300px]"><p className="text-zinc-500">読み込み中...</p></div>;
  }

  return (
    <div className="max-w-3xl" data-testid="workflow-settings-page">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-teal-700 text-white">
            <FlowArrow size={18} weight="duotone" />
          </span>
          承認ワークフロー
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          申請書ごとに必要な承認段階（署名 → 確認 → 承認）を設定します。段階の順序は固定で、適用する段階を選択できます。
        </p>
      </div>

      <div
        className={`mb-4 flex items-center justify-between gap-3 rounded-xl border p-4 ${
          enabled ? 'bg-white border-zinc-200' : 'bg-amber-50 border-amber-200'
        }`}
        data-testid="workflow-master-toggle-card"
      >
        <div className="flex items-start gap-3 min-w-0">
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${
            enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
          }`}>
            <Power size={17} weight="bold" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-zinc-900">承認ワークフロー全体を{enabled ? '有効' : '無効'}にしています</p>
            <p className="text-[12px] text-zinc-500 mt-0.5">
              無効にすると、すべての申請書で署名・確認・承認の操作と状態表示が停止されます。既存の署名記録は保持されます。
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          disabled={toggling}
          onCheckedChange={toggleEnabled}
          data-testid="workflow-enabled-switch"
          className="shrink-0"
        />
      </div>

      <div className="mb-4 flex items-start gap-2 text-[12px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
        <Info size={16} className="shrink-0 mt-0.5 text-zinc-400" />
        <span>
          <span className="font-semibold">署名</span>は申請者本人、
          <span className="font-semibold">確認</span>は管理者（確認権限）、
          <span className="font-semibold">承認</span>は承認権限を持つユーザーが実行します。選択した段階がすべて完了すると「承認済み」になります。
        </span>
      </div>

      <div className={`space-y-3 ${!enabled ? 'opacity-40 pointer-events-none select-none' : ''}`} data-testid="workflow-form-cards">
        {(meta.forms || []).map((f) => {
          const stages = draft[f.form_type] || [];
          const dirty = !sameStages(stages, original[f.form_type] || []);
          return (
            <div key={f.form_type} className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={`workflow-card-${f.form_type}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-bold text-zinc-900">{f.label}</h3>
                <div className="flex items-center gap-2">
                  {dirty && (
                    <Button size="sm" variant="ghost" onClick={() => reset(f.form_type)} data-testid={`workflow-reset-${f.form_type}`} className="text-zinc-500">
                      元に戻す
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => save(f.form_type)}
                    disabled={!dirty || savingType === f.form_type}
                    data-testid={`workflow-save-${f.form_type}`}
                    className="bg-teal-700 hover:bg-teal-800"
                  >
                    {savingType === f.form_type ? '保存中...' : '保存'}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-stretch gap-2">
                {order.map((s, i) => {
                  const active = stages.includes(s);
                  return (
                    <div key={s} className="flex items-stretch gap-2">
                      <button
                        type="button"
                        onClick={() => toggle(f.form_type, s)}
                        data-testid={`workflow-stage-${f.form_type}-${s}`}
                        aria-pressed={active}
                        className={`flex flex-col items-start text-left px-3.5 py-2.5 rounded-lg border transition-colors w-44 ${
                          active
                            ? 'border-teal-700 bg-teal-700/5'
                            : 'border-zinc-200 bg-white hover:bg-zinc-50'
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <CheckCircle
                            size={16}
                            weight={active ? 'fill' : 'regular'}
                            className={active ? 'text-teal-700' : 'text-zinc-300'}
                          />
                          <span className={`text-sm font-semibold ${active ? 'text-teal-700' : 'text-zinc-500'}`}>
                            {(meta.stage_labels && meta.stage_labels[s]) || s}
                          </span>
                        </span>
                        <span className={`text-[11px] mt-1 ${active ? 'text-zinc-500' : 'text-zinc-400'}`}>
                          {STAGE_DESC[s]}
                        </span>
                      </button>
                      {i < order.length - 1 && (
                        <span className="flex items-center text-zinc-300">
                          <ArrowRight size={16} weight="bold" />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
